import { $sType } from '#utils/index';
import {
  v2RechargeRefundRechargeDraft,
  outsourceV2RechargeRefundRechargeDraft,
  v2RechargeRefundRechargeCalcPreview,
  outsourceV2RechargeRefundRechargeCalcPreview,
  v2RechargeRefundRechargeBatchCalcPreview,
  outsourceV2RechargeRefundRechargeBatchCalcPreview,
  v2RechargeRefundRechargeBatchDraft,
  outsourceV2RechargeRefundRechargeBatchDraft,
  v2RechargeRefundRechargeOrder,
  outsourceV2RechargeRefundRechargeOrder,
  v2RechargeRefundRechargeBatch,
  outsourceV2RechargeRefundRechargeBatch,
  v2RechargeRefundRechargeConfirm,
  outsourceV2RechargeRefundRechargeConfirm,
  v2RechargeRefundRechargeBatchConfirm,
  outsourceV2RechargeRefundRechargeBatchConfirm,
  v2RechargeRefundRechargeAbandon,
  outsourceV2RechargeRefundRechargeAbandon,
  v2RechargeRefundRechargeBatchAbandon,
  v2RechargeRefundRechargeDetailByOrderId,
  outsourceV2RechargeRefundRechargeDetailByOrderId,
  v2RechargeRefundRechargeBatchDetail,
  outsourceV2RechargeRefundRechargeBatchDetail,
  type RechargeEntryEnum,
  type RechargeBatchDraftQuery,
} from '#services/api';
import type { TextAreaWithUploadValue } from './types';
import { CALC_PREVIEW_DEBOUNCE_WAIT } from './constants';

/**
 * 充值单 API 封装层。按 $sType 区分内外包接口（内包 /api/v2/，外包 /api/outsource/v2/）。
 * $sType === 'outer' → 外包端函数（outsourceV2...），否则内包端函数（v2...）。
 */

/** 是否外包端（供应商端）。⚠️ 必须函数动态判断：$sType 由 app 入口 setSType 顶层设置，静态 import 链早于 setSType 求值，
 * 模块顶层缓存 isOuter 会在外包端拿到 inner → 走内包接口（实证充值 detail 请求了 /api/v2/... 而非 /api/outsource/v2/...）。 */
const isOuter = () => $sType === 'outer';

/**
 * 充值草稿（draft）接口按 $sType 选择内/外包实现后直传。
 * api.ts 的 draft 函数已含 initiator_role/entry 参数（genapi 自后端最新 Controller），直传无需类型断言。
 */
export function fetchRechargeDraft(
  params: {
    l2_demand_id: number;
    l3_demand_id?: number;
    recharge_order_id?: number;
    entry: RechargeEntryEnum;
  },
  opts?: Parameters<typeof v2RechargeRefundRechargeDraft>[1]
) {
  const api = isOuter()
    ? outsourceV2RechargeRefundRechargeDraft
    : v2RechargeRefundRechargeDraft;
  return api(params, opts);
}

/**
 * 充值批量草稿（batch-draft）接口按 $sType 选择内/外包实现后直传。
 * 前端勾选多个二级单后整批调用，返回各行 draft 预填数据。整批成败（任一不可发起整批报错，前端 catch 不开抽屉）。
 */
export function fetchRechargeBatchDraft(
  params: RechargeBatchDraftQuery,
  opts?: Parameters<typeof v2RechargeRefundRechargeBatchDraft>[1]
) {
  const api = isOuter()
    ? outsourceV2RechargeRefundRechargeBatchDraft
    : v2RechargeRefundRechargeBatchDraft;
  return api(params, opts);
}

/** 充值核算实时预判（填金额后调，返回 is_over_charge + need_over_charge_reason） */
export const fetchRechargeCalcPreview = (
  params: Parameters<typeof v2RechargeRefundRechargeCalcPreview>[0],
  opts?: Parameters<typeof v2RechargeRefundRechargeCalcPreview>[1]
) =>
  isOuter()
    ? outsourceV2RechargeRefundRechargeCalcPreview(params, opts)
    : v2RechargeRefundRechargeCalcPreview(params, opts);

/** 充值批量核算实时预判（批量发起整批调，返回每行 is_over_charge + need_over_charge_reason） */
export const fetchRechargeBatchCalcPreview = (
  params: Parameters<typeof v2RechargeRefundRechargeBatchCalcPreview>[0],
  opts?: Parameters<typeof v2RechargeRefundRechargeBatchCalcPreview>[1]
) =>
  isOuter()
    ? outsourceV2RechargeRefundRechargeBatchCalcPreview(params, opts)
    : v2RechargeRefundRechargeBatchCalcPreview(params, opts);

// ─── 接口层防抖（§4.5.1.4：超充校验接口层防抖，避免短时间内重复调用）───
// trailing debounce：静默 CALC_PREVIEW_DEBOUNCE_WAIT 后发一次，期间多次调用合并为最后一次的入参；
// 所有 pending 调用者用最后一次结果 resolve（组件端 setTableData 为函数式更新，幂等可安全复用）。
let batchCalcTimer: ReturnType<typeof setTimeout> | null = null;
let batchCalcPendingParams: Parameters<typeof fetchRechargeBatchCalcPreview>[0] | null = null;
let batchCalcPendingOpts: Parameters<typeof fetchRechargeBatchCalcPreview>[1] | undefined;
let batchCalcResolvers: Array<{
  resolve: (v: Awaited<ReturnType<typeof fetchRechargeBatchCalcPreview>>) => void;
  reject: (e: unknown) => void;
}> = [];

/**
 * 充值批量核算实时预判（接口层防抖版）。组件 onBlur 直调本函数即可，无需自管 timer。
 */
export function fetchRechargeBatchCalcPreviewDebounced(
  params: Parameters<typeof fetchRechargeBatchCalcPreview>[0],
  opts?: Parameters<typeof fetchRechargeBatchCalcPreview>[1]
): ReturnType<typeof fetchRechargeBatchCalcPreview> {
  return new Promise((resolve, reject) => {
    batchCalcPendingParams = params;
    batchCalcPendingOpts = opts;
    batchCalcResolvers.push({ resolve, reject });
    if (batchCalcTimer) {
      clearTimeout(batchCalcTimer);
    }
    batchCalcTimer = setTimeout(() => {
      const p = batchCalcPendingParams;
      const o = batchCalcPendingOpts;
      const resolvers = batchCalcResolvers;
      batchCalcResolvers = [];
      batchCalcPendingParams = null;
      batchCalcPendingOpts = undefined;
      batchCalcTimer = null;
      fetchRechargeBatchCalcPreview(p as Parameters<typeof fetchRechargeBatchCalcPreview>[0], o)
        .then((res) => {
          resolvers.forEach((r) => r.resolve(res));
        })
        .catch((err) => {
          resolvers.forEach((r) => r.reject(err));
        });
    }, CALC_PREVIEW_DEBOUNCE_WAIT);
  });
}

/**
 * 立即冲掉接口层防抖 timer，触发 pending 的批量核算请求（提交报错重算用，跳过 500ms 等待）。
 * 若无 pending 请求则 no-op。
 */
export function flushRechargeBatchCalcPreview(): void {
  if (batchCalcTimer) {
    clearTimeout(batchCalcTimer);
    // 触发 timer 回调逻辑（复用：取 pending 调 fetch + resolve 所有 pending）
    const p = batchCalcPendingParams;
    const o = batchCalcPendingOpts;
    const resolvers = batchCalcResolvers;
    batchCalcResolvers = [];
    batchCalcPendingParams = null;
    batchCalcPendingOpts = undefined;
    batchCalcTimer = null;
    if (resolvers.length) {
      fetchRechargeBatchCalcPreview(p as Parameters<typeof fetchRechargeBatchCalcPreview>[0], o)
        .then((res) => resolvers.forEach((r) => r.resolve(res)))
        .catch((err) => resolvers.forEach((r) => r.reject(err)));
    }
  }
}

/** 发起单个充值单（落库 + 状态机启动） */
export const submitRechargeOrder = (
  data: Parameters<typeof v2RechargeRefundRechargeOrder>[0],
  opts?: Parameters<typeof v2RechargeRefundRechargeOrder>[1]
) =>
  isOuter()
    ? outsourceV2RechargeRefundRechargeOrder(data, opts)
    : v2RechargeRefundRechargeOrder(data, opts);

/** 批量发起充值单（同一一级单下多个二级单，整体回滚） */
export const submitRechargeBatch = (
  data: Parameters<typeof v2RechargeRefundRechargeBatch>[0],
  opts?: Parameters<typeof v2RechargeRefundRechargeBatch>[1]
) =>
  isOuter()
    ? outsourceV2RechargeRefundRechargeBatch(data, opts)
    : v2RechargeRefundRechargeBatch(data, opts);

/** 确认 / 退回（4 级确认 + 退回） */
export const confirmRecharge = (
  data: Parameters<typeof v2RechargeRefundRechargeConfirm>[0],
  opts?: Parameters<typeof v2RechargeRefundRechargeConfirm>[1]
) =>
  isOuter()
    ? outsourceV2RechargeRefundRechargeConfirm(data, opts)
    : v2RechargeRefundRechargeConfirm(data, opts);

/** 批量确认 / 批量退回（同一审批节点多单，spec §4.5.4，模式A整体回滚） */
export const batchConfirmRecharge = (
  data: Parameters<typeof v2RechargeRefundRechargeBatchConfirm>[0],
  opts?: Parameters<typeof v2RechargeRefundRechargeBatchConfirm>[1]
) =>
  isOuter()
    ? outsourceV2RechargeRefundRechargeBatchConfirm(data, opts)
    : v2RechargeRefundRechargeBatchConfirm(data, opts);

/** 废弃（仅待发起态） */
export const abandonRecharge = (
  params: Parameters<typeof v2RechargeRefundRechargeAbandon>[0],
  opts?: Parameters<typeof v2RechargeRefundRechargeAbandon>[1]
) =>
  isOuter()
    ? outsourceV2RechargeRefundRechargeAbandon(params, opts)
    : v2RechargeRefundRechargeAbandon(params, opts);

/** 批量废弃（仅待发起态，模式A整体回滚；内包工作台批量操作，外包无批量废弃） */
export const batchAbandonRecharge = (
  data: Parameters<typeof v2RechargeRefundRechargeBatchAbandon>[0],
  opts?: Parameters<typeof v2RechargeRefundRechargeBatchAbandon>[1]
) => v2RechargeRefundRechargeBatchAbandon(data, opts);

/** 充值单详情（展示型 VO） */
export const fetchRechargeDetail = (
  params: Parameters<typeof v2RechargeRefundRechargeDetailByOrderId>[0],
  opts?: Parameters<typeof v2RechargeRefundRechargeDetailByOrderId>[1]
) =>
  isOuter()
    ? outsourceV2RechargeRefundRechargeDetailByOrderId(params, opts)
    : v2RechargeRefundRechargeDetailByOrderId(params, opts);

/** 批量充值单详情（批量确认弹框打开时调，含账号/渠道/平台/优化师/预算/定标/执行日期等 PRD 批量确认页所需字段） */
export const fetchRechargeBatchDetail = (
  params: Parameters<typeof v2RechargeRefundRechargeBatchDetail>[0],
  opts?: Parameters<typeof v2RechargeRefundRechargeBatchDetail>[1]
) =>
  isOuter()
    ? outsourceV2RechargeRefundRechargeBatchDetail(params, opts)
    : v2RechargeRefundRechargeBatchDetail(params, opts);

/**
 * 从 TextAreaWithUpload 值提取附件 ID 列表（active_storage_attachments blob id）。
 * 适用于 over_charge_reason / early_recharge_reason / 供应商确认附件。
 */
export function extractAttachmentIds(value?: Partial<TextAreaWithUploadValue>): number[] {
  if (!value?.files?.length) return [];
  return value.files
    .map((file) => file?.blob?.id ?? file?.id)
    .filter((id): id is number => typeof id === 'number');
}

/**
 * 构建超充/提前充值原因提交字段（策划发起时填，优化师/供应商发起时不填）。
 * 返回 { reason, attachment_ids } 形态，供 RechargeSubmitCmd 组装。
 */
export function buildReasonFields(value?: Partial<TextAreaWithUploadValue>) {
  return {
    reason: value?.text ?? '',
    attachment_ids: extractAttachmentIds(value),
  };
}
